import { TrendingUp, Plus, Trash2 } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type LevelingConfig = GuildConfig["leveling"];
type LevelRole = LevelingConfig["level_roles"][number];

type Props = {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: LevelingConfig) => void;
};

function newLevelRole(): LevelRole {
  return { level: 5, role_id: "" };
}

export function LevelingTab({ config, channels, roles, onChange }: Props) {
  const cfg = config.leveling;
  const channelOptions = channels.map((ch) => ({ id: ch.id, name: ch.name }));
  const roleOptions = roles.map((r) => ({ id: r.id, name: r.name, color: r.color }));

  const update = <K extends keyof LevelingConfig>(key: K, value: LevelingConfig[K]) => {
    onChange({ ...cfg, [key]: value });
  };

  const updateRoleAt = (index: number, patch: Partial<LevelRole>) => {
    onChange({
      ...cfg,
      level_roles: cfg.level_roles.map((lr, i) => (i === index ? { ...lr, ...patch } : lr)),
    });
  };

  const addLevelRole = () => {
    onChange({ ...cfg, level_roles: [...cfg.level_roles, newLevelRole()] });
  };

  const removeLevelRoleAt = (index: number) => {
    onChange({ ...cfg, level_roles: cfg.level_roles.filter((_, i) => i !== index) });
  };

  return (
    <Section
      icon={TrendingUp}
      title="Leveling & Ranks"
      description="Members earn XP from chatting and level up — rank cards, a leaderboard, and optional role rewards."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          A member earns a random amount of XP (between the min/max below) per message, limited by the cooldown so
          spamming doesn't farm levels. <code className="text-primary">/rank</code> shows a rank card,{" "}
          <code className="text-primary">/levelboard</code> shows the server leaderboard, and staff can adjust
          someone's XP with <code className="text-primary">/setxp</code>.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Min XP per message">
          <Input
            type="number"
            min={1}
            value={cfg.xp_min}
            onChange={(e) => update("xp_min", Math.max(1, Number(e.target.value) || 1))}
          />
        </Field>
        <Field label="Max XP per message">
          <Input
            type="number"
            min={1}
            value={cfg.xp_max}
            onChange={(e) => update("xp_max", Math.max(1, Number(e.target.value) || 1))}
          />
        </Field>
        <Field label="Cooldown (seconds)" hint="Minimum time between XP-earning messages, per member.">
          <Input
            type="number"
            min={0}
            value={cfg.cooldown_seconds}
            onChange={(e) => update("cooldown_seconds", Math.max(0, Number(e.target.value) || 0))}
          />
        </Field>
      </div>

      <Field label="Ignored channels" hint="Messages here never earn XP.">
        <MultiSelect
          options={channelOptions}
          value={cfg.ignored_channel_ids}
          onChange={(v) => update("ignored_channel_ids", v)}
          prefix="#"
        />
      </Field>

      <Field label="Ignored roles" hint="Members with any of these roles never earn XP (e.g. bots-with-roles, muted).">
        <MultiSelect
          options={roleOptions}
          value={cfg.ignored_role_ids}
          onChange={(v) => update("ignored_role_ids", v)}
          prefix="@"
        />
      </Field>

      <div className="rounded-lg border border-border p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Level-up message</div>
            <div className="text-xs text-muted-foreground">
              Posted when a member levels up. Use <code>{"{user}"}</code> and <code>{"{level}"}</code>.
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm shrink-0">
            <input
              type="checkbox"
              checked={cfg.level_up_message_enabled}
              onChange={(e) => update("level_up_message_enabled", e.target.checked)}
              className="rounded"
            />
            On
          </label>
        </div>
        <Field label="Message">
          <Input
            value={cfg.level_up_message}
            onChange={(e) => update("level_up_message", e.target.value)}
            placeholder="🎉 {user} reached Level {level}!"
          />
        </Field>
        <Field label="Channel" hint="Empty = post in the same channel the member leveled up in.">
          <ChannelPicker
            channels={channels}
            value={cfg.level_up_channel_id}
            onChange={(v) => update("level_up_channel_id", v === "__none__" ? "" : v)}
          />
        </Field>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold">Level roles</h3>
          <p className="text-sm text-muted-foreground">
            Grant a role when a member reaches a level. Members keep roles from lower levels too.
          </p>
        </div>
        <Button type="button" onClick={addLevelRole}>
          <Plus className="h-4 w-4" />
          Add level role
        </Button>
      </div>

      <div className="space-y-3">
        {cfg.level_roles.length === 0 && (
          <Card className="p-4 text-sm text-muted-foreground">
            No level roles yet. Add one to start rewarding levels with roles.
          </Card>
        )}
        {cfg.level_roles.map((lr, i) => (
          <Card key={i} className="p-4 flex items-center gap-3">
            <Field label="Level">
              <Input
                type="number"
                min={1}
                className="w-24"
                value={lr.level}
                onChange={(e) => updateRoleAt(i, { level: Math.max(1, Number(e.target.value) || 1) })}
              />
            </Field>
            <Field label="Role" hint="">
              <Select
                value={lr.role_id || "__none__"}
                onValueChange={(v) => updateRoleAt(i, { role_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {roleOptions.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      @{r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="ml-auto self-end"
              onClick={() => removeLevelRoleAt(i)}
            >
              <Trash2 className="h-4 w-4" />
              Remove
            </Button>
          </Card>
        ))}
      </div>
    </Section>
  );
}
